import React, { useEffect, useState } from "react";
import "../pages/LoginPage.css";
import axios from "../axiosSetup";
import { useNavigate } from "react-router-dom";
import { getUser, isAuthenticated, setAuth } from "../auth";
import { buildAccessMapFromServices } from "../utils/roleAccess";
import { setCsrfToken } from "../csrf";

import LoginImage from "../assets/LoginPageImage.svg";
import Logo from "../assets/LoginVocovoLogo.svg";
import HumanIcon from "../assets/OrangeHumanIcon.svg";
import LockIcon from "../assets/OrangeLockIcon.svg";
import EyeIcon from "../assets/OrgangeEyeIcon.svg";
import "@fontsource/open-sans/400.css";
import "@fontsource/open-sans/600.css";
import "@fontsource/open-sans/700.css";

const REMEMBER_KEY = "vocovo_remember_me";
const REMEMBER_EXPIRY_DAYS = 30;

const getRedirectPath = (user) =>
  user?.userType === "customer" ? "/customer/dashboard" : "/dashboard";

const Login = () => {
  const navigate = useNavigate();

  const [emailPhone, setEmailPhone] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requiresRoleSelection, setRequiresRoleSelection] = useState(false);
  const [selectedLoginRole, setSelectedLoginRole] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (!saved) {
      setEmailPhone("");
      setPassword("");
      setRemember(false);
      return;
    }

    try {
      const parsed = JSON.parse(saved);
      const now = Date.now();

      if (parsed.expiry > now) {
        setEmailPhone(parsed.email || "");
        setPassword("");
        setRemember(true);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
    } catch {
      localStorage.removeItem(REMEMBER_KEY);
    }
  }, []);

  useEffect(() => {
    const loggedIn = isAuthenticated();
    const user = getUser();

    if (loggedIn && user) {
      navigate(getRedirectPath(user), { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyMinHeight = document.body.style.minHeight;

    document.documentElement.style.overflow = "auto";
    document.body.style.overflow = "auto";
    document.body.style.minHeight = "100vh";

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.minHeight = previousBodyMinHeight;
    };
  }, []);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validateForm = () => {
    const newErrors = {};

    if (!emailPhone.trim()) {
      newErrors.emailPhone = "Email is required.";
    } else if (!isValidEmail(emailPhone)) {
      newErrors.emailPhone = "Enter a valid email.";
    }

    if (!password.trim()) {
      newErrors.password = "Password is required.";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters.";
    }

    if (requiresRoleSelection && !selectedLoginRole) {
      newErrors.general = "Please select a role to continue.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getLoginIdentity = (data, submittedRole = "") => {
    const normalizedSubmittedRole = String(submittedRole || "").trim().toLowerCase();
    const backendRole = String(data?.user?.role || data?.role || "").trim().toLowerCase();
    const effectiveRole = normalizedSubmittedRole || backendRole;

    if (data?.customer) {
      return {
        role: "customer",
        userType: "customer",
        services: [],
        user: data.customer,
      };
    }

    if (effectiveRole === "customer") {
      return {
        role: "customer",
        userType: "customer",
        services: [],
        user: data?.user || data,
      };
    }

    if (data?.user || Array.isArray(data?.services) || effectiveRole === "user") {
      return {
        role: backendRole || normalizedSubmittedRole || "user",
        userType: "platformUser",
        services: data?.services || [],
        user: data?.user || data,
      };
    }

    if (data?.id || data?.email) {
      return {
        role: "customer",
        userType: "customer",
        services: [],
        user: data,
      };
    }

    return null;
  };

  const persistRememberMe = () => {
    if (remember) {
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + REMEMBER_EXPIRY_DAYS);

      localStorage.setItem(
        REMEMBER_KEY,
        JSON.stringify({
          email: emailPhone,
          expiry: expiry.getTime(),
        })
      );
      return;
    }

    localStorage.removeItem(REMEMBER_KEY);
  };

  const handleLogin = async () => {
    const existingUser = getUser();
    if (existingUser) {
      navigate(getRedirectPath(existingUser), { replace: true });
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const payload = {
        email: emailPhone,
        password,
      };

      if (requiresRoleSelection && selectedLoginRole) {
        payload.role = selectedLoginRole;
      }

      const response = await axios.post("/auth/login", payload);
      const data = response.data;

      if (!data) {
        setErrors({ general: "Login failed. Try again." });
        setLoading(false);
        return;
      }

      if (data.requiresRoleSelection) {
        setRequiresRoleSelection(true);
        setSelectedLoginRole("");
        setLoading(false);
        return;
      }

      const identity = getLoginIdentity(data, payload.role);
      if (!identity) {
        setErrors({ general: "Invalid account type. Contact support." });
        setLoading(false);
        return;
      }

      const { role, userType, services, user } = identity;
      const accessMap = buildAccessMapFromServices(services);

      persistRememberMe();

      if (data.csrfToken) setCsrfToken(data.csrfToken);

      setAuth({
        user: {
          ...user,
          role,
          userType,
          services,
          accessMap,
        },
      });

      navigate(userType === "customer" ? "/customer/dashboard" : "/dashboard", {
        replace: true,
      });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        "Invalid login credentials. Please try again.";

      setErrors({ general: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page login-page--signin">
      <div className="login-left">
        <img src={LoginImage} alt="login visual" className="login-visual" />
      </div>

      <div className="login-card">
        <div className="logo-box-wrapper">
          <div className="logo-box">
            <img src={Logo} className="login-logo" alt="logo" />
          </div>
        </div>

        <h2 className="login-title">Login</h2>

        <div className="input-group">
          <img src={HumanIcon} alt="" className="input-icon" />
          <input
            type="text"
            placeholder="Enter email"
            className="login-input"
            value={emailPhone}
            onChange={(e) => {
              setEmailPhone(e.target.value);
              setRequiresRoleSelection(false);
              setSelectedLoginRole("");
            }}
          />
        </div>
        {errors.emailPhone && <p className="error-text">{errors.emailPhone}</p>}

        <div className="input-group">
          <img src={LockIcon} alt="" className="input-icon" />
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            className="login-input"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setRequiresRoleSelection(false);
              setSelectedLoginRole("");
            }}
          />
          <img
            src={EyeIcon}
            alt=""
            className="eye-icon"
            onClick={() => setShowPassword(!showPassword)}
          />
        </div>
        {errors.password && <p className="error-text">{errors.password}</p>}

        {requiresRoleSelection && (
          <div className="input-group">
            <img src={HumanIcon} alt="" className="input-icon" />
            <select
              className="login-input login-select"
              value={selectedLoginRole}
              onChange={(e) => setSelectedLoginRole(e.target.value)}
              disabled={loading}
            >
              <option value="">Select role</option>
              <option value="user">User</option>
              <option value="customer">Customer</option>
            </select>
          </div>
        )}

        <div className="options-row">
          <label className="remember-me">
            <input
              type="checkbox"
              className="custom-checkbox"
              checked={remember}
              disabled={loading}
              onChange={() => {
                const newValue = !remember;
                setRemember(newValue);

                if (!newValue) {
                  setEmailPhone("");
                  setPassword("");
                  localStorage.removeItem(REMEMBER_KEY);
                }
              }}
            />
            Remember Me
          </label>

          <span
            className="forgot-password"
            onClick={() => (window.location.href = "/auth/forgot-password")}
          >
            Forgot password?
          </span>
        </div>

        {errors.general && <p className="error-text">{errors.general}</p>}

        <button className="login-btn" onClick={handleLogin} disabled={loading}>
          {loading ? "Processing..." : "Login to Continue"}
        </button>
      </div>
    </div>
  );
};

export default Login;
